import React from "react";
import { ClassTaxonomyNode } from "@semantical/modules/ontology-model";

// Render a hierarchical tree item
type TreeItemProps = {
    node: ClassTaxonomyNode;
    depth: number;
    expandedIds: Set<string>;
    selectedId: string | null;
    onToggle: (id: string) => void;
    onSelect: (node: ClassTaxonomyNode) => void;
    onMouseMove: (e: React.MouseEvent, id: string) => void;
    onRef: (id: string, el: HTMLDivElement | null) => void;
    query: string;
    highlightMatch: (text: string, query: string) => React.ReactNode;
};

function TreeItem({ node, depth, expandedIds, selectedId, onToggle, onSelect, onMouseMove, onRef, query, highlightMatch }: TreeItemProps) {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const isSelected = selectedId === node.id;
    const isLeaf = !!node.classType;

    const itemStyle: React.CSSProperties = {
        padding: "6px 12px",
        paddingLeft: 12 + depth * 16,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: isSelected ? "rgba(100, 255, 180, 0.15)" : "transparent",
        borderLeft: isSelected ? "2px solid #6f8" : "2px solid transparent",
        transition: "all 0.1s ease",
        fontSize: isLeaf ? 13 : 12,
        fontWeight: isLeaf ? 400 : 500,
        color: isLeaf ? "#e0e0e0" : "rgba(255,255,255,0.7)"
    };

    const chevronStyle: React.CSSProperties = {
        width: 14,
        height: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.15s ease",
        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
        opacity: hasChildren ? 1 : 0
    };

    const iconStyle: React.CSSProperties = {
        width: 16,
        height: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isLeaf ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.5)"
    };

    const handleClick = () => {
        if (hasChildren) {
            onToggle(node.id);
        } else if (node.classType) {
            onSelect(node);
        }
    };

    return (
        <>
            <div
                ref={(el) => onRef(node.id, el)}
                style={itemStyle}
                onClick={handleClick}
                onMouseMove={(e) => onMouseMove(e, node.id)}
            >
                {/* Expand/collapse chevron */}
                <div style={chevronStyle}>
                    {hasChildren && (
                        <svg width="10" height="10" viewBox="0 0 10 10">
                            <path d="M3,1 L7,5 L3,9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                    )}
                </div>

                {/* Icon */}
                <div style={iconStyle}>
                    <svg width="12" height="12" viewBox="0 0 12 12">
                        {node.icon}
                    </svg>
                </div>

                {/* Label */}
                <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {highlightMatch(node.label, query)}
                </div>

                {/* Stereotype badge for leaf nodes */}
                {isLeaf && node.stereotype && (
                    <span style={{ fontSize: 9, color: "rgba(100,255,180,0.6)", fontStyle: "italic" }}>
                        &laquo;{node.stereotype}&raquo;
                    </span>
                )}

                {/* Children count for categories */}
                {hasChildren && (
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                        {node.children!.length}
                    </span>
                )}
            </div>

            {/* Children */}
            {hasChildren && isExpanded && (
                <div style={{
                    borderLeft: "1px solid rgba(255,255,255,0.1)",
                    marginLeft: 18 + depth * 16
                }}>
                    {node.children!.map(child => (
                        <TreeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            expandedIds={expandedIds}
                            selectedId={selectedId}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            onMouseMove={onMouseMove}
                            onRef={onRef}
                            query={query}
                            highlightMatch={highlightMatch}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

// Get all leaf nodes for search
function getLeafNodes(nodes: ClassTaxonomyNode[], path: string[] = []): Array<{node: ClassTaxonomyNode; path: string[]}> {
    const result: Array<{node: ClassTaxonomyNode; path: string[]}> = [];
    for (const node of nodes) {
        const currentPath = [...path, node.label];
        if (node.classType) {
            result.push({ node, path: currentPath });
        }
        if (node.children) {
            result.push(...getLeafNodes(node.children, currentPath));
        }
    }
    return result;
}

// Fuzzy match
function fuzzyMatch(text: string, query: string): { match: boolean; score: number } {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    if (lowerText === lowerQuery) return { match: true, score: 100 };
    if (lowerText.startsWith(lowerQuery)) return { match: true, score: 90 };
    if (lowerText.includes(lowerQuery)) return { match: true, score: 70 };
    let queryIdx = 0;
    let score = 0;
    for (let i = 0; i < lowerText.length && queryIdx < lowerQuery.length; i++) {
        if (lowerText[i] === lowerQuery[queryIdx]) {
            score += 10;
            queryIdx++;
        }
    }
    if (queryIdx === lowerQuery.length) return { match: true, score };
    return { match: false, score: 0 };
}

type Props = {
    x: number;
    y: number;
    taxonomy?: ClassTaxonomyNode[];
    onSelect: (node: ClassTaxonomyNode) => void;
    onCancel: () => void;
    conceptLabel?: string;
};

type State = {
    query: string;
    selectedId: string | null;
    expandedIds: Set<string>;
    mode: "browse" | "search";
};

export default class ClassPicker extends React.Component<Props, State> {
    private inputRef = React.createRef<HTMLInputElement>();
    private containerRef = React.createRef<HTMLDivElement>();
    private listRef = React.createRef<HTMLDivElement>();
    private itemRefs: Map<string, HTMLDivElement> = new Map();
    private taxonomy: ClassTaxonomyNode[];
    private leafItems: Array<{node: ClassTaxonomyNode; path: string[]}>;
    private lastMousePosition: { x: number; y: number } | null = null;

    constructor(props: Props) {
        super(props);
        this.taxonomy = props.taxonomy || [];
        this.leafItems = getLeafNodes(this.taxonomy);

        // Start with top-level categories expanded
        const initialExpanded = new Set<string>();
        this.taxonomy.forEach(node => initialExpanded.add(node.id));

        this.state = {
            query: "",
            selectedId: null,
            expandedIds: initialExpanded,
            mode: "browse"
        };
    }

    componentDidMount() {
        setTimeout(() => this.inputRef.current?.focus(), 50);
        document.addEventListener("keydown", this.handleKeyDown);
        document.addEventListener("mousedown", this.handleClickOutside);
        if (this.containerRef.current) {
            this.containerRef.current.addEventListener("wheel", this.handleNativeWheel, { capture: true, passive: false });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
        document.removeEventListener("mousedown", this.handleClickOutside);
        if (this.containerRef.current) {
            this.containerRef.current.removeEventListener("wheel", this.handleNativeWheel, { capture: true });
        }
    }

    handleNativeWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.listRef.current) {
            this.listRef.current.scrollTop += e.deltaY;
        }
    };

    handleClickOutside = (e: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(e.target as Node)) {
            this.props.onCancel();
        }
    };

    // Get visible items in tree order (respecting expanded state)
    getVisibleItems(): ClassTaxonomyNode[] {
        const { query, expandedIds } = this.state;
        const isSearching = query.trim().length > 0;

        if (isSearching) {
            // In search mode, return filtered leaf nodes
            return this.getFilteredLeaves().map(item => item.node);
        }

        // In browse mode, return visible tree items
        const result: ClassTaxonomyNode[] = [];
        const addVisible = (nodes: ClassTaxonomyNode[]) => {
            for (const node of nodes) {
                result.push(node);
                if (node.children && expandedIds.has(node.id)) {
                    addVisible(node.children);
                }
            }
        };
        addVisible(this.taxonomy);
        return result;
    }

    // Find a node by ID in the taxonomy
    findNodeById(id: string, nodes: ClassTaxonomyNode[] = this.taxonomy): ClassTaxonomyNode | null {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.children) {
                const found = this.findNodeById(id, node.children);
                if (found) return found;
            }
        }
        return null;
    }

    // Find parent of a node
    findParentOf(id: string, nodes: ClassTaxonomyNode[] = this.taxonomy, parent: ClassTaxonomyNode | null = null): ClassTaxonomyNode | null {
        for (const node of nodes) {
            if (node.id === id) return parent;
            if (node.children) {
                const found = this.findParentOf(id, node.children, node);
                if (found !== null) return found;
            }
        }
        return null;
    }

    scrollSelectedIntoView() {
        const { selectedId } = this.state;
        if (selectedId) {
            const el = this.itemRefs.get(selectedId);
            if (el) {
                el.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
        }
    }

    handleKeyDown = (e: KeyboardEvent) => {
        const visibleItems = this.getVisibleItems();
        const { selectedId, expandedIds } = this.state;
        const currentIndex = selectedId ? visibleItems.findIndex(n => n.id === selectedId) : -1;
        const currentNode = selectedId ? this.findNodeById(selectedId) : null;

        switch (e.key) {
            case "Escape":
                e.preventDefault();
                this.props.onCancel();
                break;

            case "ArrowDown":
                e.preventDefault();
                if (visibleItems.length > 0) {
                    const nextIndex = currentIndex < visibleItems.length - 1 ? currentIndex + 1 : 0;
                    this.setState({ selectedId: visibleItems[nextIndex].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowUp":
                e.preventDefault();
                if (visibleItems.length > 0) {
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : visibleItems.length - 1;
                    this.setState({ selectedId: visibleItems[prevIndex].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowRight":
                e.preventDefault();
                // Expand current node if it has children and is collapsed
                if (currentNode && currentNode.children && !expandedIds.has(currentNode.id)) {
                    this.handleToggle(currentNode.id);
                } else if (currentNode && currentNode.children && expandedIds.has(currentNode.id)) {
                    // Already expanded, move to first child
                    this.setState({ selectedId: currentNode.children[0].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowLeft":
                e.preventDefault();
                // Collapse current node if expanded, or go to parent
                if (currentNode && currentNode.children && expandedIds.has(currentNode.id)) {
                    this.handleToggle(currentNode.id);
                } else if (selectedId) {
                    // Go to parent
                    const parent = this.findParentOf(selectedId);
                    if (parent) {
                        this.setState({ selectedId: parent.id }, () => this.scrollSelectedIntoView());
                    }
                }
                break;

            case "Enter":
                e.preventDefault();
                if (currentNode) {
                    if (currentNode.classType) {
                        // Leaf node - select it
                        this.props.onSelect(currentNode);
                    } else if (currentNode.children) {
                        // Category node - toggle expand/collapse
                        this.handleToggle(currentNode.id);
                    }
                }
                break;
        }
    };

    handleToggle = (id: string) => {
        this.setState(s => {
            const newExpanded = new Set(s.expandedIds);
            if (newExpanded.has(id)) {
                newExpanded.delete(id);
            } else {
                newExpanded.add(id);
            }
            return { expandedIds: newExpanded };
        });
    };

    handleMouseMove = (e: React.MouseEvent, id: string) => {
        // Only update selection if the mouse actually moved
        // This prevents scroll-triggered "hover" from changing selection
        const newPos = { x: e.clientX, y: e.clientY };

        if (this.lastMousePosition === null ||
            Math.abs(newPos.x - this.lastMousePosition.x) > 1 ||
            Math.abs(newPos.y - this.lastMousePosition.y) > 1) {
            this.lastMousePosition = newPos;
            this.setState({ selectedId: id });
        }
    };

    handleRef = (id: string, el: HTMLDivElement | null) => {
        if (el) {
            this.itemRefs.set(id, el);
        } else {
            this.itemRefs.delete(id);
        }
    };

    getFilteredLeaves(): Array<{node: ClassTaxonomyNode; path: string[]; score: number}> {
        const { query } = this.state;
        if (!query.trim()) return [];

        const results: Array<{node: ClassTaxonomyNode; path: string[]; score: number}> = [];
        for (const item of this.leafItems) {
            const labelMatch = fuzzyMatch(item.node.label, query);
            const descMatch = item.node.description ? fuzzyMatch(item.node.description, query) : { match: false, score: 0 };
            let keywordScore = 0;
            if (item.node.keywords) {
                for (const kw of item.node.keywords) {
                    const kwMatch = fuzzyMatch(kw, query);
                    if (kwMatch.match) keywordScore = Math.max(keywordScore, kwMatch.score * 0.8);
                }
            }
            // Also match stereotype
            const stereotypeMatch = item.node.stereotype ? fuzzyMatch(item.node.stereotype, query) : { match: false, score: 0 };
            const pathMatch = fuzzyMatch(item.path.join(" "), query);
            const bestScore = Math.max(labelMatch.score, descMatch.score * 0.7, keywordScore, stereotypeMatch.score * 0.9, pathMatch.score * 0.5);
            if (bestScore > 0) {
                results.push({ ...item, score: bestScore });
            }
        }
        return results.sort((a, b) => b.score - a.score);
    }

    highlightMatch = (text: string, query: string): React.ReactNode => {
        if (!query) return text;
        const lowerText = text.toLowerCase();
        const lowerQuery = query.toLowerCase();
        const index = lowerText.indexOf(lowerQuery);
        if (index === -1) return text;
        return (
            <>
                {text.slice(0, index)}
                <span style={{ color: "#6f8", fontWeight: 600 }}>{text.slice(index, index + query.length)}</span>
                {text.slice(index + query.length)}
            </>
        );
    };

    render() {
        const { x, y, conceptLabel } = this.props;
        const { query, selectedId, expandedIds } = this.state;
        const searchResults = this.getFilteredLeaves();
        const isSearching = query.trim().length > 0;

        const paletteWidth = 380;
        const paletteHeight = 480;

        const containerStyle: React.CSSProperties = {
            width: paletteWidth,
            maxHeight: paletteHeight,
            background: "rgba(20, 22, 28, 0.98)",
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: 13,
            color: "#e0e0e0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backdropFilter: "blur(10px)",
            animation: "classPaletteSlideIn 0.15s ease-out"
        };

        return (
            <foreignObject x={x - paletteWidth / 2} y={y - paletteHeight / 2} width={paletteWidth + 20} height={paletteHeight + 30}>
                <div ref={this.containerRef} style={containerStyle}>
                    {/* Header */}
                    <div style={{ padding: "12px 14px 8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                        {conceptLabel && (
                            <div style={{ marginBottom: 8, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                                Select type for: <span style={{ color: "#6f8", fontWeight: 500 }}>{conceptLabel}</span>
                            </div>
                        )}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "rgba(255,255,255,0.06)", borderRadius: 8,
                            padding: "8px 12px", border: "1px solid rgba(255,255,255,0.1)"
                        }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.5 }}>
                                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                ref={this.inputRef}
                                type="text"
                                placeholder="Search OWL2 class types..."
                                value={query}
                                onChange={(e) => this.setState({ query: e.target.value })}
                                style={{
                                    flex: 1, background: "transparent", border: "none",
                                    outline: "none", color: "#fff", fontSize: 14, fontFamily: "inherit"
                                }}
                            />
                            {isSearching && (
                                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                                    {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                                </span>
                            )}
                        </div>

                        {/* Mode indicator */}
                        <div style={{ marginTop: 8, display: "flex", gap: 8, fontSize: 10 }}>
                            <span style={{
                                padding: "3px 8px", borderRadius: 4,
                                background: !isSearching ? "rgba(100,255,180,0.2)" : "rgba(255,255,255,0.05)",
                                color: !isSearching ? "#6f8" : "rgba(255,255,255,0.4)"
                            }}>
                                Browse
                            </span>
                            <span style={{
                                padding: "3px 8px", borderRadius: 4,
                                background: isSearching ? "rgba(100,255,180,0.2)" : "rgba(255,255,255,0.05)",
                                color: isSearching ? "#6f8" : "rgba(255,255,255,0.4)"
                            }}>
                                Search
                            </span>
                        </div>
                    </div>

                    {/* Content */}
                    <div ref={this.listRef} style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
                        {isSearching ? (
                            // Search results (flat list)
                            searchResults.length === 0 ? (
                                <div style={{ padding: "20px 14px", textAlign: "center", color: "rgba(255,255,255,0.4)" }}>
                                    No class types found
                                </div>
                            ) : (
                                searchResults.map(item => (
                                    <div
                                        key={item.node.id}
                                        ref={(el) => this.handleRef(item.node.id, el)}
                                        style={{
                                            padding: "8px 14px", cursor: "pointer",
                                            display: "flex", alignItems: "center", gap: 10,
                                            background: selectedId === item.node.id ? "rgba(100,255,180,0.15)" : "transparent",
                                            borderLeft: selectedId === item.node.id ? "2px solid #6f8" : "2px solid transparent"
                                        }}
                                        onClick={() => item.node.classType && this.props.onSelect(item.node)}
                                        onMouseMove={(e) => this.handleMouseMove(e, item.node.id)}
                                    >
                                        <div style={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.6)" }}>
                                            <svg width="14" height="14" viewBox="0 0 12 12">{item.node.icon}</svg>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                                                {this.highlightMatch(item.node.label, query)}
                                                {item.node.stereotype && (
                                                    <span style={{ fontSize: 9, color: "rgba(100,255,180,0.6)", fontStyle: "italic" }}>
                                                        &laquo;{item.node.stereotype}&raquo;
                                                    </span>
                                                )}
                                            </div>
                                            {item.node.description && (
                                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
                                                    {this.highlightMatch(item.node.description, query)}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                                            {item.path.slice(0, -1).join(" > ")}
                                        </div>
                                    </div>
                                ))
                            )
                        ) : (
                            // Hierarchical tree view
                            this.taxonomy.map(node => (
                                <TreeItem
                                    key={node.id}
                                    node={node}
                                    depth={0}
                                    expandedIds={expandedIds}
                                    selectedId={selectedId}
                                    onToggle={this.handleToggle}
                                    onSelect={this.props.onSelect}
                                    onMouseMove={this.handleMouseMove}
                                    onRef={this.handleRef}
                                    query={query}
                                    highlightMatch={this.highlightMatch}
                                />
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: "8px 14px", borderTop: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.02)", display: "flex",
                        justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.4)"
                    }}>
                        <div style={{ display: "flex", gap: 12 }}>
                            <span>
                                <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>rdf:type</span>
                                <span style={{ marginLeft: 4 }}>instance of</span>
                            </span>
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                            <span>
                                <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>⏎</span>
                                <span style={{ marginLeft: 4 }}>select</span>
                            </span>
                            <span>
                                <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>
                                    esc
                                </span>
                                <span style={{ marginLeft: 4 }}>cancel</span>
                            </span>
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes classPaletteSlideIn {
                        from { opacity: 0; transform: translateY(-8px) scale(0.96); }
                        to { opacity: 1; transform: translateY(0) scale(1); }
                    }
                `}</style>
            </foreignObject>
        );
    }
}

export { ClassPicker };
